import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shell } from '../components/Shell';
import { getActiveTenant } from '../lib/tenant';
import { CMP_DOMAIN } from '../lib/firebase';

export function InstallPage() {
  const tenantId = getActiveTenant();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  if (!tenantId) {
    return (
      <Shell centerLabel="Install · Step 3 of 4">
        <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
          <div className="max-w-[720px] mx-auto px-6 md:px-14 py-20">
            <div className="meta mb-3" style={{ color: 'var(--flag)' }}>No workspace</div>
            <p className="text-[14px] leading-[1.55]" style={{ color: 'rgba(13,11,31,0.75)' }}>
              You haven&apos;t completed onboarding.{' '}
              <Link to="/onboarding/site" className="underline" style={{ color: 'var(--ink)' }}>Start there.</Link>
            </p>
          </div>
        </section>
      </Shell>
    );
  }

  const snippet = `<link rel="stylesheet" href="${CMP_DOMAIN}/v1/cmp.css?tenant=${tenantId}" />
<script src="${CMP_DOMAIN}/v1/cmp.js?tenant=${tenantId}" defer></script>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Shell centerLabel="Install · Step 3 of 4">
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[760px] mx-auto px-6 md:px-14 py-14">
          <span className="pill mb-3 inline-flex">
            <span className="pill-dot" /> 05 · Install · 30 seconds
          </span>
          <h1 className="font-medium mt-4 mb-3" style={{ fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.03em' }}>
            Two lines. <span className="grad-text">That&apos;s it.</span>
          </h1>
          <p className="text-[15px] leading-[1.55] mb-8" style={{ color: 'rgba(13,11,31,0.7)' }}>
            Paste into your site&apos;s &lt;head&gt;. WordPress, GTM, Webflow, Next.js, raw HTML, anywhere a script tag lives.
          </p>

          <div
            style={{
              background: 'var(--ink)',
              color: 'var(--paper)',
              borderRadius: 6,
              padding: '22px 24px',
              fontFamily: 'IBM Plex Mono, monospace',
              fontSize: 13,
              lineHeight: 1.7,
              position: 'relative',
              overflowX: 'auto',
            }}
          >
            <button
              onClick={copy}
              style={{
                position: 'absolute',
                top: 10,
                right: 12,
                background: copied ? 'transparent' : 'rgba(255,255,255,0.1)',
                color: copied ? 'var(--live)' : 'var(--paper)',
                border: `1px solid ${copied ? 'var(--live)' : 'rgba(255,255,255,0.2)'}`,
                padding: '5px 10px',
                fontFamily: 'IBM Plex Mono, monospace',
                fontSize: 10,
                letterSpacing: '1.4px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                borderRadius: 3,
              }}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{snippet}</pre>
          </div>

          <details style={{ marginTop: 22 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13 }}>Where do I paste this? ↓</summary>
            <ul style={{ margin: '14px 0 0 22px', fontSize: 13, lineHeight: 1.7 }}>
              <li>
                <strong>WordPress:</strong> Appearance → Theme File Editor → header.php, inside &lt;head&gt;.
              </li>
              <li>
                <strong>GTM:</strong> create a Custom HTML tag, fire on All Pages.
              </li>
              <li>
                <strong>Next.js / React:</strong> add to the root layout&apos;s &lt;head&gt; or use next/script.
              </li>
              <li>
                <strong>Raw HTML:</strong> paste before &lt;/head&gt; on every page.
              </li>
            </ul>
          </details>

          <div className="flex gap-3 mt-10">
            <button className="btn" onClick={() => navigate('/dashboard')}>
              <span>Go to dashboard</span> <span className="slash">/</span>
            </button>
            <a className="btn btn-ghost" href={`${CMP_DOMAIN}/v1/cmp.js?tenant=${tenantId}`} target="_blank" rel="noreferrer">
              Verify endpoint
            </a>
          </div>
        </div>
      </section>
    </Shell>
  );
}
